import { zodResolver } from "@hookform/resolvers/zod";
import { cn } from "@synthesis/shared-utils";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";

import {
  crossingObjectDataSchema,
  scrollToFirstInvalidCreateApplicationField,
  usePageStore,
  type CrossingObjectDataFormValues,
} from "../../create-application-page.lib";
import { CrossingObjectCommonSection } from "./create-application-crossing-object-common-section";
import { CrossingObjectEngineeringSection } from "./create-application-crossing-object-engineering-section";
import { CrossingObjectScheduleSection } from "./create-application-crossing-object-schedule-section";

type CreateApplicationCrossingObjectDataStepProps = {
  className?: string;
};

export const CreateApplicationCrossingObjectDataStep = observer(({ className }: CreateApplicationCrossingObjectDataStepProps) => {
  const pageStore = usePageStore();

  const methods = useForm<CrossingObjectDataFormValues>({
    resolver: zodResolver(crossingObjectDataSchema),
    mode: "onTouched",
    reValidateMode: "onChange",
    defaultValues: pageStore?.crossingObjectData,
  });

  const { handleSubmit, watch, formState } = methods;

  useEffect(() => {
    pageStore?.setObjectDataValidator(
      () =>
        new Promise<boolean>((resolve) => {
          void handleSubmit(
            () => resolve(true),
            () => {
              scrollToFirstInvalidCreateApplicationField();
              resolve(false);
            },
          )();
        }),
    );

    return () => pageStore?.setObjectDataValidator(null);
  }, [pageStore, handleSubmit]);

  useEffect(() => {
    pageStore?.setObjectDataStepValid(formState.isValid);
  }, [formState.isValid, pageStore]);

  useEffect(() => {
    const subscription = watch((values) => {
      pageStore?.setCrossingObjectData(values as CrossingObjectDataFormValues);
    });

    return () => subscription.unsubscribe();
  }, [pageStore, watch]);

  return (
    <FormProvider {...methods}>
      <form className={cn("bg-neutrals-white flex flex-col gap-8 rounded-[14px] px-6 py-8", className)}>
        <CrossingObjectCommonSection />
        <CrossingObjectEngineeringSection />
        <CrossingObjectScheduleSection />
      </form>
    </FormProvider>
  );
});
